import React, { useState } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import ReactTable from "react-table";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCubes,
  faCreditCard,
  faMoneyBill,
  faPowerOff,
  faPlusSquare
} from "@fortawesome/free-solid-svg-icons";
import { Popconfirm } from "antd";
import useSearchHook from "hooks/useSearch.hook";
import exportFromBlob from "helpers/export-from-blob";
import filterCaseInsensitive from "helpers/react-table-case-insensitive";
import {
  openCustomModal,
  closeCustomModal
} from "views/lib/components/modals";
import {
  getUsersList,
  updatePackage,
  updateTariff,
  exportUsers,
  addBonus
} from "actions/users/actions";

import UserFilers from "views/pages/users/filters";

import ModalBonus from "views/pages/users/modal-bonus";
import ModalPackage from "views/pages/users/modal-package";
import ModalTariff from "views/pages/users/modal-tariff";
import ModalNotification from "views/pages/users/modal-notification";
import filterTypes from "data/filter-types";
import ADMIN_ROLES from "../../../data/admin-roles";

const FILTERS_KEY = "usersList";

const Users = (props) => {
  const {
    closeCustomModal,
    openCustomModal,
    addBonus,
    updatePackage,
    updateTariff,
    exportUsers,
    getUsersList,
    usersList,
    roles,
  } = props;
  const {
    page,
    size,
    filters,
    handleSearch,
    handleReset,
    handlePageChange,
    handleSortedChange,
    handlePageSizeChange
  } = useSearchHook(getUsersList, {
    size: 20,
    sort: "id,asc",
    name: FILTERS_KEY,
  });
  const [usersArr, setUsersArr] = useState([]);

  const handleSelectUser = (userId) => {
    if (userId === false) {
      setUsersArr([]);
      return;
    }
    const index = usersArr.indexOf(userId);
    if (index === -1) {
      setUsersArr([...usersArr, userId]);
    } else {
      setUsersArr(usersArr.filter(i => i !== userId));
    }
  };

  const modalBonus = (original) => {
    const modalObj = {
      id: `modalBonus-${original.id}`,
      modalSettings: {
        size: "sm"
      },
      header: "Bonus",
      children: (
        <ModalBonus
          onCancel={() => closeCustomModal(modalObj)}
          onSubmit={(value) => {
            addBonus({
              amount: value,
              userId: original.id,
            });
            closeCustomModal(modalObj);
          }}
        />
      ),
      handleClose: () => closeCustomModal(modalObj)
    };
    openCustomModal(modalObj);
  };

  const modalPackage = (userId, packId) => {
    const modalObj = {
      modalSettings: {
        size: "sm"
      },
      header: "Package",
      children: (
        <ModalPackage
          currentPackageId={packId || false}
          onCancel={() => closeCustomModal(modalObj)}
          onSubmit={(pack) => {
            updatePackage(userId, pack);
            closeCustomModal(modalObj);
          }}
        />
      ),
      handleClose: () => closeCustomModal(modalObj)
    };
    openCustomModal(modalObj);
  };

  const modalTariff = (userId, tariffId) => {
    const modalObj = {
      modalSettings: {
        size: "sm"
      },
      header: "Tariff",
      children: (
        <ModalTariff
          currentTariffId={tariffId || false}
          onCancel={() => closeCustomModal(modalObj)}
          onSubmit={(tariff, until) => {
            updateTariff(userId, tariff, until);
            closeCustomModal(modalObj);
          }}
        />
      ),
      handleClose: () => closeCustomModal(modalObj)
    };
    openCustomModal(modalObj);
  };

  const handleExport = () => {
    exportUsers(filters, (data) => {
      const fileName = `users-${moment()
        .format("YYYY-MM-DD")}.xls`;
      exportFromBlob(data, fileName);
    });
  };

  const handleNotificationModal = () => {
    const localFilters = [...filters];
    if (usersArr.length) {
      localFilters.push({
        fieldName: "id",
        fieldValue: usersArr,
        searchOperation: filterTypes.IN.value
      });
    }
    const modalObj = {
      modalSettings: {
        size: "lg",
        style: {
          width: 1200,
          maxWidth: "100%",
          padding: 20
        }
      },
      header: "Send notification",
      children: (
        <ModalNotification
          onCancel={() => closeCustomModal(modalObj)}
          onSubmit={() => closeCustomModal(modalObj)}
          filters={localFilters}
        />
      ),
      handleClose: () => closeCustomModal(modalObj)
    };
    openCustomModal(modalObj);
  };

  const getColumns = () => [
    {
      sortable: false,
      filterable: false,
      resizable: false,
      minResizeWidth: 40,
      width: 40,
      // eslint-disable-next-line react/display-name
      Cell: ({ original }) => (
        <input
          type="checkbox"
          className="checkbox"
          checked={usersArr.indexOf(original.id) > -1}
          onChange={() => handleSelectUser(original.id)}
        />
      ),
      // eslint-disable-next-line react/display-name
      Header: () => (
        <div
          className={usersArr.length ? "" : "d-none"}
          title={`Reset all (${usersArr.length}) selected users?`}
        >
          <Popconfirm
            title={`Reset all (${usersArr.length}) selected users?`}
            onConfirm={() => handleSelectUser(false)}
            placement="bottomLeft"
            okText="Yes"
            cancelText="Cancel"
          >
            <button className="btn btn-sm btn-danger">
              <FontAwesomeIcon icon={faPowerOff}/>
            </button>
          </Popconfirm>
        </div>
      )
    },
    {
      id: "id",
      Header: "#",
      accessor: "id",
      // eslint-disable-next-line react/display-name
      Cell: ({ original }) => (
        <Link to={`/users/${original.id}`}>
          {original.id}
        </Link>
      ),
    },
    {
      id: "name",
      Header: "First Name",
      accessor: original => (original.name || ""),
      Cell: ({ original }) => (original.name || ""),
    },
    {
      id: "surname",
      Header: "Last Name",
      accessor: original => (original.surname || ""),
      Cell: ({ original }) => (original.surname || ""),
    },
    {
      id: "email",
      Header: "Email",
      accessor: "email",
    },
    {
      id: "birthday",
      Header: "Birthday",
      accessor: "birthday",
      Cell: ({ original }) => (original.birthday || "Not Set"),
    },
    {
      id: "device",
      Header: "Device(last)",
      Cell: ({ original }) => (Array.isArray(original.devices) && original.devices.length ? original.devices[original.devices.length - 1].model : ""),
      sortable: false
    },
    {
      id: "os",
      Header: "OS Version(last)",
      Cell: ({ original }) => (Array.isArray(original.devices) && original.devices.length ? original.devices[original.devices.length - 1].osVersion : ""),
      sortable: false
    },
    {
      id: "lastActionDate",
      Header: "Last Action",
      accessor: "lastActionDate",
      Cell: ({ original }) => (original.lastActionDate
        ? moment(original.lastActionDate)
          .format("HH:mm YYYY-MM-DD")
        : "Not Set"),
    },
    {
      id: "age",
      Header: "Age",
      accessor: "age",
      Cell: ({ original }) => (original.birthday
        ? moment()
        .format("YYYY") - moment(original.birthday)
        .format("YYYY")
        : "Not Set"),
      sortable: false
    },
    {
      id: "gender",
      Header: "Gender",
      accessor: "gender",
      // eslint-disable-next-line react/display-name
      Cell: ({ original }) => <div className="text-lowercase">{original.gender}</div>,
    },
    {
      id: "created",
      Header: "Created",
      accessor: original => moment(original.created)
        .format("YYYY-MM-DD"),
      Cell: ({ original }) => moment(original.created)
        .format("YYYY-MM-DD"),
    },
    {
      id: "student",
      Header: "Student",
      Cell: ({ original }) => (original.student ? "Yes" : "No")
    },
    {
      id: "tags",
      Header: "Tags",
      Cell: ({ original }) => (original.tags
        ? original.tags.map(item => <span className="badge badge-warning" key={item}>{item}</span>)
        : ""),
      style: {
        whiteSpace: "unset"
      }
    },
    {
      id: "has-card",
      Header: "Has Card",
      // eslint-disable-next-line react/display-name
      Cell: ({ original }) => (
        <div className={`${original.hasCard ? "text-success" : "text-danger"}`}>
          <FontAwesomeIcon icon={faCreditCard}/>
        </div>
      )
    },
    {
      id: "actions",
      Header: "Actions",
      className: "text-center",
      headerClassName: "text-center",
      width: 120,
      // eslint-disable-next-line react/display-name
      Cell: ({ original }) => (
        <div className="d-flex justify-content-center">
          {roles.includes(ADMIN_ROLES.admin) && (
            <div
              className="d-flex text-warning g-pointer mr-2 ml-2"
              onClick={() => modalBonus(original)}
              style={{ fontSize: 18 }}
              title="Bonus"
            >
              <FontAwesomeIcon icon={faPlusSquare}/>
            </div>
          )}
          <div
            className="d-flex text-warning g-pointer mr-2 ml-2"
            onClick={() => modalPackage(
              original.id,
              (original.pack ? original.pack.id : null)
            )}
            style={{ fontSize: 18 }}
            title="Package"
          >
            <FontAwesomeIcon icon={faCubes}/>
          </div>
          <div
            className="d-flex text-warning g-pointer mr-2 ml-2"
            onClick={() => modalTariff(
              original.id,
              (original.tariff ? original.tariff.id : null)
            )}
            style={{ fontSize: 18 }}
            title="Tariff"
          >
            <FontAwesomeIcon icon={faMoneyBill}/>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="w-100 p-2">
      <div className="filters">
        <UserFilers
          name={FILTERS_KEY}
          activeFilters={filters}
          totalCount={usersList.total_count || 0}
          handleSearch={handleSearch}
          handleReset={handleReset}
          handleExport={handleExport}
          handleNotificationModal={handleNotificationModal}
        />
      </div>

      <ReactTable
        data={usersList.content || []}
        headerClassName="table-header"
        columns={getColumns()}
        pageSizeOptions={[10, 20, 50, 100]}
        resizable
        className="-striped -highlight"
        loading={false}
        filterable={false}
        defaultFilterMethod={filterCaseInsensitive}
        getNoDataProps={() => ({
          style: {
            zIndex: 0
          }
        })}
        style={{
          height: "calc(100vh - 140px)"
        }}
        manual={true}
        pages={usersList.total_pages}
        page={page}
        pageSize={size}
        onSortedChange={handleSortedChange}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />
    </div>
  );
};

const mapStateToProps = state => ({
  usersList: state.users.usersList,
  roles: state.auth.roles
});

const mapStateToDispatch = dispatch => ({
  openCustomModal: bindActionCreators(openCustomModal, dispatch),
  closeCustomModal: bindActionCreators(closeCustomModal, dispatch),

  getUsersList: bindActionCreators(getUsersList, dispatch),
  updatePackage: bindActionCreators(updatePackage, dispatch),
  updateTariff: bindActionCreators(updateTariff, dispatch),
  exportUsers: bindActionCreators(exportUsers, dispatch),
  addBonus: bindActionCreators(addBonus, dispatch),
});

export default connect(
  mapStateToProps,
  mapStateToDispatch
)(Users);



// WEBPACK FOOTER //
// ./src/views/pages/users/index.js